/**
 * useMicroformField - CyberSource Microform v2 field lifecycle hook
 *
 * @module hooks/useMicroformField
 * @memberof -Common
 */
import { useEffect, useRef, useState } from 'react';

/**
 * Manages lifecycle of a single Microform hosted field.
 *
 * @param {object} options
 * @param {object} options.microform - The Microform instance from initMicroform()
 * @param {string} options.fieldType - Either 'number' or 'securityCode'
 * @param {string} [options.selector] - Optional CSS selector to load the field into
 * @param {function} [options.onChange] - Optional callback triggered on field changes
 * @param {any} [options.refreshTrigger] - Optional trigger to re-load the field (e.g., when an overlay closes)
 * @returns {object} { containerRef, valid, empty, cardType, focused, fieldInstance }
 */
export const useMicroformField = ( options ) => {
  const { microform, fieldType, selector, onChange, show, refreshTrigger, maxLength, placeholder, title } = options || {};

  const [valid, setValid] = useState( false );
  const [couldBeValid, setCouldBeValid] = useState( true );
  const [empty, setEmpty] = useState( true );
  const [cardType, setCardType] = useState( '' );
  const [focused, setFocused] = useState( false );
  const [error, setError] = useState( null );

  const containerRef = useRef( null );
  const fieldRef = useRef( null );
  const onChangeRef = useRef( onChange );

  useEffect( () => {
    onChangeRef.current = onChange;
  }, [onChange] );

  useEffect( () => {
    if( !microform || !fieldType || !show ){
      return;
    }

    // Check if field already exists on this microform instance (from singleton cache)
    let field = microform._fields?.[fieldType];
    let isNewField = false;
    const microformStyles = {
      input: {
        'font-family': '"Circular", sans-serif',
        'font-size': '1rem',
        'padding': '1rem'
      },
      ':focus': {
        'color': '#232323'
      },
      ':disabled': {
        'color': '#9b9b9b'
      },
      'invalid': {
        'color': '#c20000'
      }
    };

    if( !field ){
      try {
        const fieldOptions = { styles: microformStyles, maxLength, placeholder, title };
        field = microform.createField( fieldType, fieldOptions );
        isNewField = true;
        field._state = { valid: false, couldBeValid: true, empty: true, cardType: '', focused: false, error: null };
        if( microform._fields ){
          microform._fields[fieldType] = field;
        }
      }
      catch ( e ){
        setError( e.message || 'Failed to create microform field' );
        return;
      }
    }
    else {
      // Update existing field options (like maxLength) if they have changed
      try {
        if( typeof field.update === 'function' ){
          field.update( { styles: microformStyles, maxLength, placeholder, title } );
        }
      }
      catch ( e ){
        // Fallback or non-critical error handling
      }

      // The iframe loses its state when unmounted and remounted into the DOM.
      // Reset our local state to reflect the empty iframe.
      field._state = { valid: false, couldBeValid: true, empty: true, cardType: '', focused: false, error: null };

      setValid( false );
      setCouldBeValid( true );
      setEmpty( true );
      setCardType( '' );
      setFocused( false );
      setError( null );

      if( typeof onChangeRef.current === 'function' ){
        onChangeRef.current( { valid: false, couldBeValid: true, empty: true } );
      }
    }

    fieldRef.current = field;

    let target = selector || containerRef.current;
    if( typeof target === 'string' ){
      target = document.querySelector( target );
    }

    if( target ){
      const loadField = () => {
        try {
          field.load( target );
          field._lastOwnerRef = containerRef;
        }
        catch ( e ){
          if( e.message?.includes( 'already been loaded' ) ){
            // If it's already loaded, try to unload it first then load again.
            // This can happen during fast re-renders or if multiple components share a field instance.
            try {
              field.unload();
              field.load( target );
              field._lastOwnerRef = containerRef;
            }
            catch ( retryError ){
              setError( retryError.message || 'Failed to re-load microform field' );
            }
          }
          else {
            setError( e.message || 'Failed to load microform field' );
          }

          if( field._state ){
            field._state.error = e.message || 'Failed to load microform field';
          }
        }
      };

      loadField();
    }

    // Update the field with the latest state setters for the currently mounted component
    field._latestHandlers = {
      setValid,
      setCouldBeValid,
      setEmpty,
      setCardType,
      setFocused,
      setError,
      onChangeRef
    };

    // Only attach listeners once per field instance life
    if( isNewField ){
      field.on( 'change', ( data ) => {
        const newCardType = data.card?.[0]?.name || '';

        field._state.valid = data.valid;
        field._state.couldBeValid = data.couldBeValid;
        field._state.empty = data.empty;
        field._state.cardType = newCardType;

        const handlers = field._latestHandlers;
        if( handlers ){
          handlers.setValid( data.valid );
          handlers.setCouldBeValid( data.couldBeValid );
          handlers.setEmpty( data.empty );

          if( fieldType === 'number' ){
            handlers.setCardType( newCardType );
          }

          if( handlers.onChangeRef && typeof handlers.onChangeRef.current === 'function' ){
            handlers.onChangeRef.current( data );
          }
        }
      } );

      field.on( 'blur', () => {
        field._state.focused = false;
        if( field._latestHandlers ){
          field._latestHandlers.setFocused( false );
        }
      } );
      field.on( 'focus', () => {
        field._state.focused = true;
        if( field._latestHandlers ){
          field._latestHandlers.setFocused( true );
        }
      } );
      field.on( 'error', ( data ) => {
        const errorMessage = data?.message || 'A field error occurred';
        field._state.error = errorMessage;
        if( field._latestHandlers ){
          field._latestHandlers.setError( errorMessage );
        }
      } );
    }

    return () => {
      if( field && typeof field.unload === 'function' ){
        try {
          // Only unload if we are the one who last loaded it.
          // This prevents unmounting components from stealing the field from newly mounted ones.
          if( field._lastOwnerRef === containerRef ){
            field.unload();
            field._lastOwnerRef = null;
          }
        }
        catch ( e ){
          // Ignore "Field is not loaded" errors during unmount/cleanup
        }
      }
    };
  }, [microform, fieldType, selector, show, refreshTrigger, maxLength, placeholder, title, containerRef.current] );

  return {
    containerRef,
    valid,
    couldBeValid,
    empty,
    cardType,
    focused,
    error,
    fieldInstance: fieldRef.current
  };
};

export default useMicroformField;
